import { Injectable, inject } from '@angular/core';
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { parseProductPrice } from '../../models/billing-product.type';
import {
  BillItem,
  isBillDueDate,
  patientAccountBalance,
} from '../../models/patient-bill.type';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { MembershipService } from '../auth/membership.service';

export const RECEIVABLES_PAGE_SIZE = 20;

export interface ReceivableRow {
  id: string;
  name: string;
  amountOwed: number;
}

export interface ReceivablesPage {
  businessId: string;
  rows: ReceivableRow[];
  /** Cursor for the next page. Present when this page returned a row. */
  last: QueryDocumentSnapshot | null;
  hasNext: boolean;
}

@Injectable({ providedIn: 'root' })
export class PatientBalanceService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private membership = inject(MembershipService);
  /** Businesses whose patients already have an amountOwed field. */
  private ensuredBusinessIds = new Set<string>();

  /**
   * Writes the patient's billed-minus-paid balance so cuentas por cobrar can
   * order and page without reading every factura.
   */
  async recomputeAmountOwed(
    patientId: string,
    businessId = this.membership.requireSelectedBusinessId()
  ): Promise<number> {
    const [billsSnap, paymentsSnap] = await Promise.all([
      getDocs(collection(this.firestore, 'businesses', businessId, 'patients', patientId, 'bills')),
      getDocs(collection(this.firestore, 'businesses', businessId, 'patients', patientId, 'payments')),
    ]);
    const { difference } = patientAccountBalance(
      parseBills(billsSnap.docs.map((bill) => bill.data())),
      parsePayments(paymentsSnap.docs.map((payment) => payment.data()))
    );
    await updateDoc(doc(this.firestore, 'businesses', businessId, 'patients', patientId), {
      amountOwed: difference,
    });
    return difference;
  }

  /** Fills amountOwed for patients created before the field existed. Once per clinic per session. */
  async ensureAmountOwed(): Promise<void> {
    const businessId = this.membership.requireSelectedBusinessId();
    if (this.ensuredBusinessIds.has(businessId)) {
      return;
    }
    const snap = await getDocs(collection(this.firestore, 'businesses', businessId, 'patients'));
    const missing = snap.docs.filter((patient) => typeof patient.data()['amountOwed'] !== 'number');
    for (let index = 0; index < missing.length; index += 5) {
      if (this.membership.selectedBusinessId() !== businessId) {
        return;
      }
      await Promise.all(
        missing.slice(index, index + 5).map((patient) => this.recomputeAmountOwed(patient.id, businessId))
      );
    }
    if (this.membership.selectedBusinessId() !== businessId) {
      return;
    }
    this.ensuredBusinessIds.add(businessId);
  }

  async listReceivables(cursor: QueryDocumentSnapshot | null): Promise<ReceivablesPage> {
    const businessId = this.membership.requireSelectedBusinessId();
    const constraints: QueryConstraint[] = [
      where('amountOwed', '>', 0),
      orderBy('amountOwed', 'desc'),
      limit(RECEIVABLES_PAGE_SIZE + 1),
    ];
    if (cursor) {
      constraints.push(startAfter(cursor));
    }
    const snap = await getDocs(
      query(collection(this.firestore, 'businesses', businessId, 'patients'), ...constraints)
    );
    const hasNext = snap.docs.length > RECEIVABLES_PAGE_SIZE;
    const docs = hasNext ? snap.docs.slice(0, RECEIVABLES_PAGE_SIZE) : snap.docs;
    return {
      businessId,
      hasNext,
      last: docs.at(-1) ?? null,
      rows: docs.map((patient) => ({
        id: patient.id,
        name: patientName(patient.data()),
        amountOwed: Number(patient.data()['amountOwed']),
      })),
    };
  }
}

function patientName(data: Record<string, unknown>): string {
  return typeof data['name'] === 'string' && data['name'].trim() ? data['name'].trim() : 'Paciente';
}

function parseBills(docs: Record<string, unknown>[]): { items: BillItem[] }[] {
  return docs
    .map((data) => ({ items: parseBillItems(data['items']) }))
    .filter((bill) => bill.items.length > 0);
}

function parseBillItems(value: unknown): BillItem[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const items: BillItem[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const productId = typeof record['productId'] === 'string' ? record['productId'].trim() : '';
    const name = typeof record['name'] === 'string' ? record['name'].trim() : '';
    const quantity = typeof record['quantity'] === 'number' ? record['quantity'] : 0;
    const unitPrice = typeof record['unitPrice'] === 'number' ? record['unitPrice'] : NaN;
    if (productId && name && quantity >= 1 && Number.isFinite(unitPrice) && unitPrice >= 0) {
      items.push({ productId, name, quantity, unitPrice });
    }
  }
  return items;
}

function parsePayments(docs: Record<string, unknown>[]): { amount: number }[] {
  const payments: { amount: number }[] = [];
  for (const data of docs) {
    const amount = typeof data['amount'] === 'number' ? parseProductPrice(data['amount']) : null;
    const paidOn = typeof data['paidOn'] === 'string' ? data['paidOn'] : '';
    if (amount != null && amount > 0 && isBillDueDate(paidOn)) {
      payments.push({ amount });
    }
  }
  return payments;
}
